export interface Certification {
  id: string;
  name: string;
  description: string;
  validity: string;
  requirements: number;
  certifiedUsers: number;
  created: string;
  status: 'active' | 'inactive';
}

export interface Learner {
  id: string;
  name: string;
  team: string;
  role: string;
  progress: number;
  completed: number;
  total: number;
  status: 'certified' | 'in progress' | 'not started';
  certifiedDate?: string;
  expiryDate?: string;
}

export interface CertificationStats {
  totalCertifications: string;
  certifiedUsers: string;
  expiringSoon: string;
  completionRate: string;
}

export const getCertifications = async (): Promise<{
  success: boolean;
  data?: Certification[];
  error?: { message: string } | null;
}> => {
  try {
    // ===== USING MOCK DATA =====
    const response: Certification[] = [
      {
        id: '1',
        name: 'Leadership Excellence Certificate',
        description: 'Comprehensive leadership development certification',
        validity: '12 months',
        requirements: 4,
        certifiedUsers: 234,
        created: '3 months ago',
        status: 'active',
      },
      {
        id: '2',
        name: 'Data Science Professional',
        description: 'Advanced data analytics and machine learning certification',
        validity: '24 months',
        requirements: 4,
        certifiedUsers: 156,
        created: '6 months ago',
        status: 'active',
      },
      {
        id: '3',
        name: 'Compliance & Safety Certification',
        description: 'Mandatory compliance and workplace safety training',
        validity: '12 months',
        requirements: 4,
        certifiedUsers: 892,
        created: '1 year ago',
        status: 'active',
      },
      {
        id: '4',
        name: 'Project Management Certification',
        description: 'PMP-aligned project management certification',
        validity: '36 months',
        requirements: 4,
        certifiedUsers: 178,
        created: '8 months ago',
        status: 'active',
      },
    ];

    await new Promise(resolve => setTimeout(resolve, 500));
    return {
      success: true,
      data: response,
      error: null
    };

    // ===== API IMPLEMENTATION (Currently commented out) =====
    // const response = await httpClient.get<Certification[]>(ENDPOINTS.CERTIFICATIONS);
    // return { success: true, data: response, error: null };

  } catch (error: any) {
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to fetch certifications' }
    };
  }
};

export const getLearnerProgress = async (): Promise<{
  success: boolean;
  data?: Learner[];
  error?: { message: string } | null;
}> => {
  try {
    // ===== USING MOCK DATA =====
    const response: Learner[] = [
      {
        id: '1',
        name: 'John Doe',
        team: 'Engineering',
        role: 'Team Lead',
        progress: 100,
        completed: 3,
        total: 3,
        status: 'certified',
        certifiedDate: '2024-11-15',
        expiryDate: '2025-11-15',
      },
      {
        id: '2',
        name: 'Jane Smith',
        team: 'Engineering',
        role: 'Senior Engineer',
        progress: 67,
        completed: 2,
        total: 3,
        status: 'in progress',
      },
      {
        id: '3',
        name: 'Emily Rodriguez',
        team: 'Marketing',
        role: 'Marketing Manager',
        progress: 0,
        completed: 0,
        total: 3,
        status: 'not started',
      },
    ];

    await new Promise(resolve => setTimeout(resolve, 600));
    return {
      success: true,
      data: response,
      error: null
    };

    // ===== API IMPLEMENTATION (Currently commented out) =====
    // const response = await httpClient.get<Learner[]>(ENDPOINTS.LEARNER_PROGRESS);
    // return { success: true, data: response, error: null };

  } catch (error: any) {
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to fetch learner progress' }
    };
  }
};

export const getCertificationStats = async (): Promise<{
  success: boolean;
  data?: CertificationStats;
  error?: { message: string } | null;
}> => {
  try {
    // ===== USING MOCK DATA =====
    const response: CertificationStats = {
      totalCertifications: '4',
      certifiedUsers: '1,460',
      expiringSoon: '1',
      completionRate: '78%',
    };

    await new Promise(resolve => setTimeout(resolve, 400));
    return {
      success: true,
      data: response,
      error: null
    };

    // ===== API IMPLEMENTATION (Currently commented out) =====
    // const response = await httpClient.get<CertificationStats>(ENDPOINTS.CERTIFICATION_STATS);
    // return { success: true, data: response, error: null };

  } catch (error: any) {
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to fetch certification stats' }
    };
  }
};
